import h from 'hyperscript';

import type { Column, ResponsiveTable } from '@disy/ui.responsive-table/responsive-table';

const WRAPPED_CELL_START_LINE_CSS_VAR = '--wrapped-cell-start-line';
const PREFIX_HEADING_CELL_STYLE = 'wrapped-column-heading';

/**
 * Column behavior when there is not enough width to display table without horizontal scrolling.
 * By default, the columns are kept, meaning they will not react to not enough space.
 * When set to 'wrap', the column will be wrapped around, column header cell will be hidden and
 * column data cells will be placed underneath main column cells. In addition, contents of hidden
 * column header cell will be prepended to each data cell which is wrapped.
 * When set to 'hide', the column will simply be hidden when there is no enough space.
 */
export type ColumnResponsiveBehavior = 'wrap' | 'hide';
type ResponsiveColumn = Column<unknown> & { responsiveBehavior: ColumnResponsiveBehavior };

const COLUMNS_CSS_VAR = '--grid-columns';
const CELL_PADDING_HORIZONTAL_CSS_VAR = '--cell-padding-horizontal';
const DEFAULT_COLUMN_MIN_WIDTH = 200; // in px

export class ResponsiveTableResizeHandler implements EventListenerObject {
  readonly _responsiveTable;
  readonly _table;
  readonly _columns;
  readonly _selection;

  readonly _wrappedOrHiddenColumns: ResponsiveColumn[] = [];
  readonly _observer: ResizeObserver = new ResizeObserver(() => this._onTableResize());

  constructor(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    responsiveTable: ResponsiveTable<any>,
    table: HTMLTableElement,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    columns: Column<any>[],
    selection: boolean,
  ) {
    this._responsiveTable = responsiveTable;
    this._table = table;
    this._columns = columns;
    this._selection = selection;
  }

  initialize() {
    if (this._columns.some(isResponsiveColumn)) {
      this._observer.observe(this._responsiveTable);
    }
    this._updateColumnsCssVariable(this._columns);
  }

  destroy() {
    this._observer.disconnect();
  }

  handleEvent() {
    // in case table data was re-drawn, we need to re-wrap wrapped cells
    this._wrappedOrHiddenColumns.forEach((column) => {
      const columnIndex = this._columns.indexOf(column);
      const cellIndex = this._selection ? columnIndex + 1 : columnIndex;
      this._adjustColumnCells(cellIndex, column, true);
    });
  }

  _onTableResize() {
    const currentTableWidth = this._responsiveTable.offsetWidth;
    const visibleColumnsRequiredWidth = getMinRequiredWidthForColumns(this._getVisibleColumns());
    const remainingTableWidth = currentTableWidth - visibleColumnsRequiredWidth;

    if (remainingTableWidth < 0) {
      // visible columns need to be wrapped/hidden
      this._wrapOrHideColumns(Math.abs(remainingTableWidth));
    } else if (this._wrappedOrHiddenColumns.length > 0 && this._canLastWrappedColumnFit(remainingTableWidth)) {
      // check whether there is enough space to move some columns back
      this._unwrapColumns(Math.abs(remainingTableWidth));
    }
    this._updateColumnsCssVariable(this._getVisibleColumns());
    this._updateIndentationOfWrappedRows();
    this._table.classList.toggle('wrap', this._wrappedOrHiddenColumns.length > 0); // don't extract to const, wrappedColumns length might change
  }

  _wrapOrHideColumns(overflowedWidth: number) {
    let columnIndex = this._columns.length - 1;
    while (overflowedWidth > 0 && columnIndex >= 0) {
      const column = this._columns[columnIndex];
      if (isResponsiveColumn(column) && !this._wrappedOrHiddenColumns.includes(column)) {
        const headingCell = this._getHeadingCellByColumnKey(column.key);
        if (headingCell) {
          // There's no heading cell if showHeader=false.
          headingCell.classList.add(column.responsiveBehavior);
          headingCell.setAttribute('aria-hidden', column.responsiveBehavior === 'wrap' ? 'false' : 'true'); // do not hide column from screen readers
        }
        const cellIndex = this._selection ? columnIndex + 1 : columnIndex;
        this._adjustColumnCells(cellIndex, column, true);
        overflowedWidth -= getMinRequiredWidthForColumns([column]);
        this._wrappedOrHiddenColumns.push(column);
      }
      columnIndex--;
    }
  }

  _unwrapColumns(availableWidth: number) {
    let wrappedColumn: ResponsiveColumn | undefined;
    while (availableWidth > 0 && (wrappedColumn = this._wrappedOrHiddenColumns.pop())) {
      const wrappedColumnWidth = getMinRequiredWidthForColumns([wrappedColumn]);
      let headingCell = this._getHeadingCellByColumnKey(wrappedColumn.key);
      if (headingCell) {
        // There's no heading cell if showHeader=false.
        headingCell.classList.remove(wrappedColumn.responsiveBehavior);
        headingCell.removeAttribute('aria-hidden');
      }
      const wrappedColumnIndex = this._columns.indexOf(wrappedColumn);
      const cellIndex = this._selection ? wrappedColumnIndex + 1 : wrappedColumnIndex;
      this._adjustColumnCells(cellIndex, wrappedColumn, false);
      availableWidth -= wrappedColumnWidth;
    }
  }

  _adjustColumnCells(cellIndex: number, { name, responsiveBehavior }: ResponsiveColumn, isWrapping: boolean) {
    this._getCellsMap()
      .map((rowCells) => rowCells[cellIndex])
      .filter((cell) => cell !== undefined) // When using custom row renderers the number of cells per row is not constant anymore. Thus, rowCells[cellIndex] can be null. We fix this here by simply ignoring these cases...
      .forEach((cell) => {
        cell.classList.toggle(responsiveBehavior, isWrapping); // columns already passed isResponsiveColumn check
        if (responsiveBehavior === 'wrap') {
          if (isWrapping) {
            cell.prepend(h(`span.${PREFIX_HEADING_CELL_STYLE}`, { attrs: { 'aria-hidden': 'true' } }, name));
          } else {
            cell.querySelector(`.${PREFIX_HEADING_CELL_STYLE}`)?.remove();
          }
        }
      });
  }

  _updateIndentationOfWrappedRows() {
    const columns = this._getVisibleColumns();
    const mainColumnGridIndex = columns.findIndex((c) => c.main) + 1;
    this._table.style.setProperty(
      WRAPPED_CELL_START_LINE_CSS_VAR,
      String(this._selection ? mainColumnGridIndex + 1 : mainColumnGridIndex),
    );
  }

  _canLastWrappedColumnFit(remainingTableWidth: number) {
    const columnToUnwrap = this._wrappedOrHiddenColumns[this._wrappedOrHiddenColumns.length - 1];
    const requiredWidth = getMinRequiredWidthForColumns([columnToUnwrap]);
    return remainingTableWidth - requiredWidth >= 0;
  }

  _updateColumnsCssVariable(columns: Column<unknown>[]) {
    const gridColumnsTemplate = columns
      .map((c) => getColumnsWidthAsString(c, this._getCellPaddingVariableValue()))
      .join(' ');

    this._responsiveTable.style.setProperty(COLUMNS_CSS_VAR, gridColumnsTemplate);
  }

  _getCellPaddingVariableValue() {
    const paddingValue = parseInt(
      getComputedStyle(this._responsiveTable).getPropertyValue(CELL_PADDING_HORIZONTAL_CSS_VAR),
    );
    return Number.isInteger(paddingValue) ? paddingValue : 0;
  }

  _getCellsMap() {
    return [...this._table.querySelectorAll('tbody tr')].map((row) => row.querySelectorAll('td, th'));
  }

  _getHeadingCellByColumnKey(key: string) {
    return this._table.querySelector(`th[data-key='${key}']`)!;
  }

  _getVisibleColumns() {
    return this._columns.filter(
      (column) => !(isResponsiveColumn(column) && this._wrappedOrHiddenColumns.includes(column)),
    );
  }
}

/**
 * Columns don't need to have strict width definitions. Their width can be defined as integer value,
 * which represents ratio of remaining free space to be assigned to that column.
 * For columns like that, When deciding whether table width is enough to display all columns, we need
 * some minimum width required for that column.
 * This function calculates how much width in pixels is required for given columns.
 * The algorithm to determine width required for column is as follows:
 *   - if the width of the column is defined as css length in pixels (e.g 100px), then that is used
 *   - if width is integer value, then check whether resizingRequiredMinWidth is given to the column
 *     and if yes, use that value, otherwise use value DEFAULT_COLUMN_MIN_WIDTH.
 *
 * @param columns
 * @return requiredWidth
 */
function getMinRequiredWidthForColumns(columns: Column<unknown>[]) {
  return columns.reduce((columnsRequiredWidth: number, { width, resizingRequiredMinWidth }) => {
    let reqWidth;
    if (typeof width === 'string') {
      reqWidth = parseInt(width);
    } else {
      reqWidth =
        typeof resizingRequiredMinWidth === 'string' ? parseInt(resizingRequiredMinWidth) : DEFAULT_COLUMN_MIN_WIDTH;
    }
    return columnsRequiredWidth + reqWidth;
  }, 0);
}

function getColumnsWidthAsString({ width = 1 }: Column<unknown>, cellPadding = 0) {
  return typeof width === 'string' ? `${parseInt(width) + cellPadding * 2}px` : `${width}fr`;
}

function isResponsiveColumn(column: Column<unknown>): column is ResponsiveColumn {
  return !column.main && column.responsiveBehavior !== undefined;
}
